import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaPhoneAlt,
  FaWhatsapp,
  FaSearch,
  FaFilter,
  FaFileCsv,
  FaSyncAlt,
  FaMapMarkerAlt,
  FaCar,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaStickyNote,
  FaTrashAlt,
  FaArrowRight,
  FaCalendarAlt,
  FaUserCheck,
  FaUserClock,
  FaCoins,
  FaChartLine,
  FaTimes,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { API_URL } from "../../config/apiConfig";
import socketService from "../../utils/socketService";

const AbandonedBookings = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAbandoned: 0,
    totalContacted: 0,
    totalConverted: 0,
    totalArchived: 0,
    totalAll: 0,
  });

  // Filter & Pagination States
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [limit, setLimit] = useState(15);

  // Notes Modal State
  const [selectedLeadForNotes, setSelectedLeadForNotes] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  // Delete Confirmation Modal State
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [deletingLead, setDeletingLead] = useState(false);

  // Status updating map
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  const getAuthHeaders = () => {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("adminToken") ||
      "";
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", limit);
      if (statusFilter) params.append("status", statusFilter);
      if (searchQuery.trim()) params.append("search", searchQuery.trim());
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);

      const res = await axios.get(
        `${API_URL}/abandoned-bookings?${params.toString()}`,
        getAuthHeaders()
      );

      if (res.data && res.data.success) {
        setLeads(res.data.data || []);
        if (res.data.pagination) {
          setTotalPages(res.data.pagination.pages || 1);
          setTotalLeads(res.data.pagination.total || 0);
        }
        if (res.data.stats) {
          setStats(res.data.stats);
        }
      }
    } catch (err) {
      console.error("Error fetching abandoned leads:", err);
      toast.error(
        err.response?.data?.message || "Failed to load booking inquiries"
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, statusFilter, searchQuery, dateFrom, dateTo]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Real-time Socket.IO listener for live lead alerts
  useEffect(() => {
    const socket = socketService.connect();
    if (socket) {
      const handleLiveLead = () => {
        // Refresh leads and stats in background when new lead drops in
        fetchLeads();
      };

      socket.on("abandonedBookingCreated", handleLiveLead);
      return () => {
        socket.off("abandonedBookingCreated", handleLiveLead);
      };
    }
  }, [fetchLeads]);

  // Handle Quick Status Change
  const handleStatusChange = async (id, newStatus) => {
    setUpdatingStatusId(id);
    try {
      const res = await axios.put(
        `${API_URL}/abandoned-bookings/${id}/status`,
        { status: newStatus },
        getAuthHeaders()
      );
      if (res.data && res.data.success) {
        toast.success(`Status updated to "${newStatus}"`);
        setLeads((prev) =>
          prev.map((lead) =>
            lead._id === id
              ? {
                  ...lead,
                  status: newStatus,
                  contactedAt:
                    newStatus === "contacted" && !lead.contactedAt
                      ? new Date().toISOString()
                      : lead.contactedAt,
                  convertedAt:
                    newStatus === "converted" && !lead.convertedAt
                      ? new Date().toISOString()
                      : lead.convertedAt,
                }
              : lead
          )
        );
        // Refresh summary stats
        fetchLeads();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // Open Notes Modal
  const openNotesModal = (lead) => {
    setSelectedLeadForNotes(lead);
    setNoteText(lead.notes || "");
  };

  // Save Notes
  const handleSaveNotes = async () => {
    if (!selectedLeadForNotes) return;
    setSavingNotes(true);
    try {
      const res = await axios.put(
        `${API_URL}/abandoned-bookings/${selectedLeadForNotes._id}/status`,
        { notes: noteText },
        getAuthHeaders()
      );
      if (res.data && res.data.success) {
        toast.success("Notes saved successfully");
        setLeads((prev) =>
          prev.map((lead) =>
            lead._id === selectedLeadForNotes._id
              ? { ...lead, notes: noteText }
              : lead
          )
        );
        setSelectedLeadForNotes(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  // Delete Lead
  const confirmDeleteLead = async () => {
    if (!leadToDelete) return;
    setDeletingLead(true);
    try {
      const res = await axios.delete(
        `${API_URL}/abandoned-bookings/${leadToDelete._id}`,
        getAuthHeaders()
      );
      if (res.data && res.data.success) {
        toast.success("Lead removed successfully");
        setLeads((prev) => prev.filter((l) => l._id !== leadToDelete._id));
        setLeadToDelete(null);
        fetchLeads();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete lead");
    } finally {
      setDeletingLead(false);
    }
  };

  // 1-Click WhatsApp Trigger
  const handleWhatsApp = (lead) => {
    const rawPhone = String(lead.phone || "").replace(/\D/g, "");
    if (!rawPhone || rawPhone.length < 10) {
      toast.warning("Phone number not available or invalid");
      return;
    }
    const cleanPhone = rawPhone.slice(-10);
    const formattedPhone = `91${cleanPhone}`;

    const name = lead.name || "Customer";
    const pickup = lead.pickupLocation?.address || "your pickup location";
    const drop = lead.dropLocation?.address || "destination";
    const cab = lead.selectedCab?.cabName || "Cab";
    const fare = lead.estimatedPrice ? `₹${lead.estimatedPrice}` : "";

    const message = encodeURIComponent(
      `Hello ${name}! 👋\n\nWe noticed you were checking ${cab} from ${pickup} to ${drop}${
        fare ? ` (approx. ${fare})` : ""
      } on CabZigo but didn't complete the booking.\n\nNeed any help or special discount? Reply to this message or give us a call, and we'll be happy to assist you!`
    );

    window.open(`https://wa.me/${formattedPhone}?text=${message}`, "_blank");

    // Automatically mark as contacted if currently abandoned
    if (lead.status === "abandoned") {
      handleStatusChange(lead._id, "contacted");
    }
  };

  // 1-Click Book Ride with Customer's Inquiry Data
  const handleBookRideForLead = (lead) => {
    const from = lead.pickupLocation?.address || "";
    const to = lead.dropLocation?.address || "";
    const tripType = lead.tripType || "oneWay";
    const date = lead.pickupDate || "";
    const time = lead.pickupTime || "";
    const returnDate = lead.returnDate || "";
    const returnTime = lead.returnTime || "";
    const name = lead.name || "";
    const phone = lead.phone || "";
    const email = lead.email || "";

    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (tripType) params.set("tripType", tripType);
    if (date) params.set("date", date);
    if (time) params.set("time", time);
    if (returnDate) params.set("returnDate", returnDate);
    if (returnTime) params.set("returnTime", returnTime);
    if (phone) params.set("phone", phone);
    if (name) params.set("name", name);
    if (email) params.set("email", email);

    // Save prefill data so customer details page picks it up automatically
    if (phone) localStorage.setItem("prefill_booking_phone", phone);
    if (name) localStorage.setItem("prefill_booking_name", name);
    if (email) localStorage.setItem("prefill_booking_email", email);

    if (lead.status === "abandoned") {
      handleStatusChange(lead._id, "contacted");
    }

    toast.success(`Opening booking with ${name || phone}'s inquiry data!`);
    window.open(`/cabs?${params.toString()}`, "_blank");
  };

  // 1-Click Direct Phone Call
  const handlePhoneCall = (lead) => {
    const rawPhone = String(lead.phone || "").replace(/\D/g, "");
    if (!rawPhone || rawPhone.length < 10) {
      toast.warning("Phone number not available");
      return;
    }
    window.location.href = `tel:+91${rawPhone.slice(-10)}`;

    if (lead.status === "abandoned") {
      handleStatusChange(lead._id, "contacted");
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    if (leads.length === 0) {
      toast.info("No leads available to export");
      return;
    }

    const headers = [
      "Date & Time",
      "Name",
      "Phone",
      "Email",
      "Trip Type",
      "Pickup Address",
      "Drop Address",
      "Selected Cab",
      "Estimated Fare (INR)",
      "Distance",
      "Exit Page",
      "Status",
      "Contacted Date",
      "Converted Date",
      "Notes",
    ];

    const rows = leads.map((l) => [
      `"${new Date(l.createdAt).toLocaleString()}"`,
      `"${(l.name || "").replace(/"/g, '""')}"`,
      `"${l.phone || ""}"`,
      `"${(l.email || "").replace(/"/g, '""')}"`,
      `"${l.tripType || "one-way"}"`,
      `"${(l.pickupLocation?.address || "").replace(/"/g, '""')}"`,
      `"${(l.dropLocation?.address || "").replace(/"/g, '""')}"`,
      `"${(l.selectedCab?.cabName || "").replace(/"/g, '""')}"`,
      l.estimatedPrice || 0,
      `"${l.distance || ""}"`,
      `"${(l.exitPage || "").replace(/"/g, '""')}"`,
      `"${l.status || "abandoned"}"`,
      `"${l.contactedAt ? new Date(l.contactedAt).toLocaleString() : ""}"`,
      `"${l.convertedAt ? new Date(l.convertedAt).toLocaleString() : ""}"`,
      `"${(l.notes || "").replace(/"/g, '""')}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `abandoned_leads_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV export downloaded successfully");
  };

  const getExitPageBadge = (page) => {
    if (!page) return null;
    if (page.includes("payment")) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
          Payment Checkout (High Intent)
        </span>
      );
    }
    if (page.includes("customer-details")) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
          Customer Details
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200">
        Cab Selection
      </span>
    );
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "converted":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
            <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-green-500"></span>
            Converted
          </span>
        );
      case "contacted":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
            <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-blue-500"></span>
            Contacted
          </span>
        );
      case "archived":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
            <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-gray-500"></span>
            Archived
          </span>
        );
      case "abandoned":
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
            <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-amber-500"></span>
            Abandoned
          </span>
        );
    }
  };

  const recoveryRate =
    stats.totalAll > 0
      ? ((stats.totalConverted / stats.totalAll) * 100).toFixed(1)
      : 0;

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/90 p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-500/10 text-amber-600 rounded-2xl flex items-center justify-center text-xl shrink-0 shadow-sm border border-amber-200/60">
            <FaUserClock />
          </div>
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">
                Booking Inquiries & Recovery
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                Live Funnel
              </span>
            </div>
            <div className="text-xs md:text-sm text-slate-500 font-medium mt-1">
              Customer booking inquiries & dropped-off rides. Call, WhatsApp, or Book rides directly with customer's saved inquiry data.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => fetchLeads()}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-xl text-xs sm:text-sm font-bold text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-sm disabled:opacity-60"
            title="Refresh list"
          >
            <FaSyncAlt className={loading ? "animate-spin text-blue-600" : "text-slate-400"} />
            Refresh
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs sm:text-sm font-bold transition-all shadow-sm shadow-emerald-600/20 active:scale-95"
          >
            <FaFileCsv className="text-base" />
            Export CSV
          </button>
        </div>
      </div>

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Total Leads */}
        <div
          onClick={() => {
            setStatusFilter("everything");
            setCurrentPage(1);
          }}
          className={`bg-white rounded-2xl border p-4 sm:p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-pointer ${
            statusFilter === "everything"
              ? "ring-2 ring-purple-500 border-purple-200 bg-purple-50/20"
              : "border-slate-200/90"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Total Leads
            </span>
            <div className="w-10 h-10 rounded-xl bg-purple-100/80 text-purple-600 flex items-center justify-center text-base shadow-sm">
              <FaCoins />
            </div>
          </div>
          <div className="mt-2 text-3xl font-black text-slate-900 tracking-tight">
            {stats.totalAll}
          </div>
          <div className="mt-1 text-xs text-slate-400 font-medium">
            All captured inquiries
          </div>
        </div>

        {/* Abandoned (Pending) */}
        <div
          onClick={() => {
            setStatusFilter("abandoned");
            setCurrentPage(1);
          }}
          className={`bg-white rounded-2xl border p-4 sm:p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-pointer ${
            statusFilter === "abandoned"
              ? "ring-2 ring-amber-500 border-amber-200 bg-amber-50/20"
              : "border-slate-200/90"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-amber-700 uppercase tracking-wider">
              Abandoned
            </span>
            <div className="w-10 h-10 rounded-xl bg-amber-100/80 text-amber-600 flex items-center justify-center text-base shadow-sm">
              <FaClock />
            </div>
          </div>
          <div className="mt-2 text-3xl font-black text-amber-600 tracking-tight">
            {stats.totalAbandoned}
          </div>
          <div className="mt-1 text-xs text-amber-600/70 font-medium">
            Pending outreach
          </div>
        </div>

        {/* Contacted */}
        <div
          onClick={() => {
            setStatusFilter("contacted");
            setCurrentPage(1);
          }}
          className={`bg-white rounded-2xl border p-4 sm:p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-pointer ${
            statusFilter === "contacted"
              ? "ring-2 ring-blue-500 border-blue-200 bg-blue-50/20"
              : "border-slate-200/90"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wider">
              Contacted
            </span>
            <div className="w-10 h-10 rounded-xl bg-blue-100/80 text-blue-600 flex items-center justify-center text-base shadow-sm">
              <FaPhoneAlt />
            </div>
          </div>
          <div className="mt-2 text-3xl font-black text-blue-600 tracking-tight">
            {stats.totalContacted}
          </div>
          <div className="mt-1 text-xs text-blue-600/70 font-medium">
            In discussion
          </div>
        </div>

        {/* Converted */}
        <div
          onClick={() => {
            setStatusFilter("converted");
            setCurrentPage(1);
          }}
          className={`bg-white rounded-2xl border p-4 sm:p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-pointer ${
            statusFilter === "converted"
              ? "ring-2 ring-emerald-500 border-emerald-200 bg-emerald-50/20"
              : "border-slate-200/90"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
              Converted
            </span>
            <div className="w-10 h-10 rounded-xl bg-emerald-100/80 text-emerald-600 flex items-center justify-center text-base shadow-sm">
              <FaCheckCircle />
            </div>
          </div>
          <div className="mt-2 text-3xl font-black text-emerald-600 tracking-tight">
            {stats.totalConverted}
          </div>
          <div className="mt-1 text-xs text-emerald-600/70 font-medium">
            Completed bookings
          </div>
        </div>

        {/* Recovery Rate */}
        <div className="bg-white rounded-2xl border border-slate-200/90 p-4 sm:p-5 shadow-sm col-span-2 sm:col-span-1">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider">
              Recovery Rate
            </span>
            <div className="w-10 h-10 rounded-xl bg-indigo-100/80 text-indigo-600 flex items-center justify-center text-base shadow-sm">
              <FaChartLine />
            </div>
          </div>
          <div className="mt-2 text-3xl font-black text-indigo-600 tracking-tight">
            {recoveryRate}%
          </div>
          <div className="mt-1 text-xs text-indigo-600/70 font-medium">
            Inquiries converted to rides
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/90 p-4 sm:p-5 space-y-4">
        {/* Status Tabs */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3">
          <span className="text-xs font-bold text-slate-500 mr-1 flex items-center gap-1.5">
            <FaFilter className="text-slate-400" /> Filter:
          </span>
          {[
            {
              id: "all",
              label: "Active Inquiries",
              count: stats.totalAbandoned + stats.totalContacted,
            },
            { id: "abandoned", label: "Abandoned", count: stats.totalAbandoned },
            { id: "contacted", label: "Contacted", count: stats.totalContacted },
            { id: "converted", label: "Converted", count: stats.totalConverted },
            { id: "archived", label: "Archived", count: stats.totalArchived },
            { id: "everything", label: "All Inquiries", count: stats.totalAll },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setStatusFilter(tab.id);
                setCurrentPage(1);
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all duration-150 flex items-center gap-1.5 ${
                statusFilter === tab.id
                  ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200/80 hover:text-slate-800"
              }`}
            >
              <span>{tab.label}</span>
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${
                  statusFilter === tab.id
                    ? "bg-white/20 text-white"
                    : "bg-slate-200 text-slate-600"
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
          {/* Search */}
          <div className="relative md:col-span-6">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              placeholder="Search by phone, customer name, pickup, drop or exit page..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-9 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
              >
                <FaTimes size={12} />
              </button>
            )}
          </div>

          {/* Date Range */}
          <div className="md:col-span-4 flex items-center gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full py-2 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
              title="From date"
            />
            <span className="text-slate-400 text-xs font-bold">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full py-2 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
              title="To date"
            />
          </div>

          {/* Clear Filter & Count */}
          <div className="md:col-span-2 flex items-center justify-between md:justify-end gap-2">
            {(searchQuery || dateFrom || dateTo || statusFilter !== "all") && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setDateFrom("");
                  setDateTo("");
                  setStatusFilter("all");
                  setCurrentPage(1);
                }}
                className="text-xs text-red-600 hover:text-red-700 font-bold hover:underline"
              >
                Reset
              </button>
            )}
            <span className="text-xs text-slate-500 font-bold whitespace-nowrap">
              Showing {leads.length} of {totalLeads}
            </span>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/90 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
            <thead className="bg-slate-50/80 text-slate-500 font-bold text-[11px] uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th scope="col" className="px-5 py-3.5">Customer</th>
                <th scope="col" className="px-5 py-3.5">Route & Schedule</th>
                <th scope="col" className="px-5 py-3.5">Cab & Fare</th>
                <th scope="col" className="px-5 py-3.5">Drop-off Stage</th>
                <th scope="col" className="px-5 py-3.5">Status</th>
                <th scope="col" className="px-5 py-3.5">Notes</th>
                <th scope="col" className="px-5 py-3.5 text-right">Quick Outreach</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-16 text-center text-slate-500">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                      <span className="text-sm font-semibold text-slate-600">Loading leads...</span>
                    </div>
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-16 text-center text-slate-400">
                    <div className="max-w-md mx-auto flex flex-col items-center">
                      <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center text-xl mb-3 shadow-inner">
                        <FaSearch />
                      </div>
                      <p className="font-bold text-slate-700 text-base">No booking inquiries found</p>
                      <p className="text-xs text-slate-400 mt-1 max-w-xs">
                        Try adjusting your search criteria, date filter, or selected status tab.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                leads.map((lead) => {
                  const rawPhone = String(lead.phone || "").replace(/\D/g, "").slice(-10);
                  const isUpdatingThis = updatingStatusId === lead._id;

                  return (
                    <tr
                      key={lead._id}
                      className="hover:bg-blue-50/40 transition-colors"
                    >
                      {/* Customer Info */}
                      <td className="px-4 py-3.5 align-top">
                        <div className="font-semibold text-gray-900">
                          {lead.name || "Guest Lead"}
                        </div>
                        {rawPhone ? (
                          <div className="text-xs font-mono text-blue-600 font-medium mt-0.5 flex items-center gap-1">
                            <FaPhoneAlt className="text-[10px]" />
                            <a
                              href={`tel:+91${rawPhone}`}
                              className="hover:underline"
                            >
                              +91 {rawPhone}
                            </a>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">No phone</div>
                        )}
                        {lead.email && (
                          <div className="text-xs text-gray-400 truncate max-w-[150px]">
                            {lead.email}
                          </div>
                        )}
                        <div className="text-[10px] text-gray-400 mt-1">
                          {new Date(lead.createdAt).toLocaleString()}
                        </div>
                      </td>

                      {/* Route & Schedule */}
                      <td className="px-4 py-3.5 align-top max-w-[260px]">
                        <div className="space-y-1">
                          <div className="flex items-start gap-1.5 text-xs text-gray-800">
                            <span className="text-emerald-500 mt-0.5 shrink-0">●</span>
                            <span className="line-clamp-2" title={lead.pickupLocation?.address}>
                              {lead.pickupLocation?.address || "Pickup location"}
                            </span>
                          </div>
                          <div className="flex items-start gap-1.5 text-xs text-gray-800">
                            <span className="text-red-500 mt-0.5 shrink-0">■</span>
                            <span className="line-clamp-2" title={lead.dropLocation?.address}>
                              {lead.dropLocation?.address || "Drop location"}
                            </span>
                          </div>
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <span className="inline-block px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] uppercase font-medium">
                            {lead.tripType || "one-way"}
                          </span>
                          {lead.pickupDate && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                              <FaCalendarAlt className="text-[10px]" />
                              {lead.pickupDate} {lead.pickupTime}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Cab & Fare */}
                      <td className="px-4 py-3.5 align-top">
                        <div className="font-medium text-gray-800 text-xs flex items-center gap-1">
                          <FaCar className="text-blue-500" />
                          {lead.selectedCab?.cabName || "Selected Cab"}
                        </div>
                        <div className="text-base font-bold text-emerald-700 mt-1">
                          ₹{lead.estimatedPrice ? lead.estimatedPrice.toLocaleString("en-IN") : "—"}
                        </div>
                        {lead.distance && (
                          <div className="text-[11px] text-gray-400">
                            {lead.distance} {lead.duration ? `• ${lead.duration}` : ""}
                          </div>
                        )}
                      </td>

                      {/* Drop-off Stage */}
                      <td className="px-4 py-3.5 align-top">
                        <div>{getExitPageBadge(lead.exitPage)}</div>
                        <div className="text-[11px] text-gray-400 font-mono mt-1 truncate max-w-[140px]" title={lead.exitPage}>
                          {lead.exitPage || "/cabs"}
                        </div>
                      </td>

                      {/* Status Dropdown */}
                      <td className="px-4 py-3.5 align-top">
                        <div className="space-y-1.5">
                          <select
                            value={lead.status || "abandoned"}
                            onChange={(e) => handleStatusChange(lead._id, e.target.value)}
                            disabled={isUpdatingThis}
                            className={`text-xs font-semibold rounded-lg px-2.5 py-1.5 border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              lead.status === "converted"
                                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                : lead.status === "contacted"
                                ? "bg-blue-50 text-blue-800 border-blue-200"
                                : lead.status === "archived"
                                ? "bg-gray-100 text-gray-700 border-gray-300"
                                : "bg-amber-50 text-amber-800 border-amber-200"
                            }`}
                          >
                            <option value="abandoned">Abandoned</option>
                            <option value="contacted">Contacted</option>
                            <option value="converted">Converted</option>
                            <option value="archived">Archived</option>
                          </select>

                          {lead.contactedAt && (
                            <div className="text-[10px] text-gray-400">
                              Contacted: {new Date(lead.contactedAt).toLocaleDateString()}
                            </div>
                          )}
                          {lead.convertedAt && (
                            <div className="text-[10px] text-emerald-600 font-medium">
                              Converted: {new Date(lead.convertedAt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Notes */}
                      <td className="px-4 py-3.5 align-top max-w-[160px]">
                        <button
                          onClick={() => openNotesModal(lead)}
                          className="flex items-start gap-1 text-left text-xs text-gray-600 hover:text-blue-600 group"
                        >
                          <FaStickyNote className="mt-0.5 text-gray-400 group-hover:text-blue-500 shrink-0" />
                          <span className="line-clamp-2">
                            {lead.notes ? lead.notes : <span className="italic text-gray-400">Add note...</span>}
                          </span>
                        </button>
                      </td>

                      {/* Quick Outreach CTAs */}
                      <td className="px-4 py-3.5 align-top text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* 1-Click Book Ride with Customer's Inquiry Data */}
                          <button
                            onClick={() => handleBookRideForLead(lead)}
                            className="px-2.5 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-all shadow-sm flex items-center gap-1.5 font-bold text-xs"
                            title="Book ride with customer's inquiry data"
                          >
                            <FaCar className="text-xs" />
                            <span>Book Ride</span>
                          </button>

                          {/* 1-Click WhatsApp */}
                          <button
                            onClick={() => handleWhatsApp(lead)}
                            className="p-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-lg transition-colors shadow-sm"
                            title="1-Click WhatsApp customer"
                          >
                            <FaWhatsapp className="text-base" />
                          </button>

                          {/* 1-Click Call */}
                          <button
                            onClick={() => handlePhoneCall(lead)}
                            className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-colors shadow-sm"
                            title="Call customer directly"
                          >
                            <FaPhoneAlt className="text-sm" />
                          </button>

                          {/* Delete Lead */}
                          <button
                            onClick={() => setLeadToDelete(lead)}
                            className="p-2 bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                            title="Delete lead"
                          >
                            <FaTrashAlt className="text-xs" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        <div className="px-5 py-3.5 border-t border-slate-200 bg-slate-50/70 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2 text-slate-500 font-medium">
            <span>Show</span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-slate-300 rounded-lg px-2.5 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-slate-700"
            >
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
            <span>inquiries per page</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-slate-500 font-medium">
              Page <span className="font-bold text-slate-800">{currentPage}</span> of{" "}
              <span className="font-bold text-slate-800">{totalPages}</span>
            </span>
            <div className="flex gap-1.5">
              <button
                disabled={currentPage <= 1 || loading}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                className="px-3 py-1.5 border border-slate-200 rounded-xl bg-white hover:bg-slate-50 disabled:opacity-50 font-bold text-slate-700 shadow-sm transition"
              >
                Previous
              </button>
              <button
                disabled={currentPage >= totalPages || loading}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                className="px-3 py-1.5 border border-slate-200 rounded-xl bg-white hover:bg-slate-50 disabled:opacity-50 font-bold text-slate-700 shadow-sm transition"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Follow-up Notes Modal */}
      {selectedLeadForNotes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in-slide-up border border-slate-100">
            <div className="flex items-center justify-between pb-3.5 border-b border-slate-100">
              <div className="flex items-center gap-2.5 text-slate-900 font-black text-base">
                <FaStickyNote className="text-blue-500" />
                Customer Follow-up Notes
              </div>
              <button
                onClick={() => setSelectedLeadForNotes(null)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition"
              >
                <FaTimes />
              </button>
            </div>

            <div className="mt-4 text-xs text-slate-600 space-y-1.5 bg-slate-50 p-3 rounded-xl border border-slate-100">
              <div>
                <span className="font-bold text-slate-800">Lead:</span>{" "}
                {selectedLeadForNotes.name || "Customer"} ({selectedLeadForNotes.phone})
              </div>
              <div>
                <span className="font-bold text-slate-800">Route:</span>{" "}
                {selectedLeadForNotes.pickupLocation?.address} →{" "}
                {selectedLeadForNotes.dropLocation?.address}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Internal Notes (Conversation / Discount Offered):
              </label>
              <textarea
                rows="4"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="e.g., Called customer at 3 PM. Offered 10% discount coupon. Will confirm by tomorrow."
                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition"
              ></textarea>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setSelectedLeadForNotes(null)}
                className="px-4 py-2 border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition shadow-sm shadow-blue-500/20 disabled:opacity-60"
              >
                {savingNotes ? "Saving..." : "Save Notes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {leadToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-fade-in-slide-up border border-slate-100">
            <div className="flex items-center gap-3 text-red-600 font-black text-base mb-2">
              <FaTrashAlt />
              Delete Lead
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              Are you sure you want to delete lead for{" "}
              <strong className="text-slate-900">{leadToDelete.name || leadToDelete.phone}</strong>? This action cannot be undone.
            </p>
            <div className="mt-5 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setLeadToDelete(null)}
                className="px-4 py-2 border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteLead}
                disabled={deletingLead}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold transition shadow-sm shadow-red-500/20 disabled:opacity-60"
              >
                {deletingLead ? "Deleting..." : "Delete Lead"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbandonedBookings;
